import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TeamCreditsStackSourceEnum } from "@/enums/team-credits";
import { useTranslation } from "@/hooks/useTranslation";
import { TeamCreditsHistoryDTO } from "@shaarp/common";
import { ColumnDef } from "@tanstack/react-table";
import {
  ChevronDownIcon,
  ExternalLink,
  InfoIcon,
  Trash2Icon,
} from "lucide-react";
import { useState } from "react";
import { DialogAdditionalData } from "../admin/dialog-additional-data";
import DataTableCellTeamCreditsHistoryUser from "./data-table-cell-team-credits-history-user";

export const dataTableTeamCreditsHistoryColumns = (
  onRemove?: (transaction: TeamCreditsHistoryDTO) => void
): ColumnDef<TeamCreditsHistoryDTO>[] => {
  const { t } = useTranslation();
  const [selectedAdditionalData, setSelectedAdditionalData] =
    useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = (additionalData: any) => {
    setSelectedAdditionalData(additionalData);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAdditionalData(null);
  };

  return [
    {
      id: "Date",
      accessorKey: "date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t(
            "admin.dataTableAdminTeams.dialogs.giftCredits.history.columns.date"
          )}
        />
      ),
      cell: ({ cell }) => {
        const history = cell.row.original as TeamCreditsHistoryDTO;
        return new Date(history.date).toLocaleString();
      },
    },
    {
      id: "Pool",
      accessorKey: "pool",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t(
            "admin.dataTableAdminTeams.dialogs.giftCredits.history.columns.pool"
          )}
        />
      ),
      cell: ({ cell }) => {
        const history = cell.row.original as TeamCreditsHistoryDTO;
        const isApi = history.pool === "API_PUBLIC";
        return (
          <Badge variant={isApi ? "secondary" : "outline"}>
            {isApi
              ? t(
                  "admin.dataTableAdminTeams.dialogs.giftCredits.poolSelector.api"
                )
              : t(
                  "admin.dataTableAdminTeams.dialogs.giftCredits.poolSelector.main"
                )}
          </Badge>
        );
      },
    },
    {
      id: "Action",
      accessorKey: "action",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t(
            "admin.dataTableAdminTeams.dialogs.giftCredits.history.columns.action"
          )}
        />
      ),
      cell: ({ cell }) => {
        const history = cell.row.original as TeamCreditsHistoryDTO;

        const isPurchase =
          history.action === "ADD" &&
          history.sourceStack === TeamCreditsStackSourceEnum.PURCHASE;
        const isGift =
          history.action === "ADD" &&
          history.sourceStack === TeamCreditsStackSourceEnum.GIFT;
        const isConsume = history.action === "USE";

        return (
          <div className="flex flex-col flex-nowrap gap-2 items-start">
            {isPurchase ? (
              <Badge variant="default">
                {t(
                  "admin.dataTableAdminTeams.dialogs.giftCredits.history.actions.purchase"
                )}
              </Badge>
            ) : isGift ? (
              <Badge variant="outline">
                {t(
                  "admin.dataTableAdminTeams.dialogs.giftCredits.history.actions.gift"
                )}
              </Badge>
            ) : isConsume ? (
              <Badge variant="secondary">
                {t(
                  "admin.dataTableAdminTeams.dialogs.giftCredits.history.actions.consume"
                )}
              </Badge>
            ) : (
              <Badge variant="outline">{history.action}</Badge>
            )}
          </div>
        );
      },
    },
    {
      id: "Type",
      accessorKey: "type",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t(
            "admin.dataTableAdminTeams.dialogs.giftCredits.history.columns.type"
          )}
        />
      ),
      cell: ({ cell }) => {
        const history = cell.row.original as TeamCreditsHistoryDTO;

        const typeLabels: Record<string, string> = {
          PHONE_RETRIEVAL: t(
            "admin.dataTableAdminTeams.dialogs.giftCredits.history.types.phoneRetrieval"
          ),
          API_GET_COMPANY: "Get Company",
          API_SEARCH_COMPANIES: "Search Companies",
          API_AI_SEARCH: "AI Search",
          API_SIMILARS: "Similars",
          API_EMPLOYEES: "Employees",
          API_REVEAL_EMAIL: "Reveal Email",
          API_REVEAL_PHONE: "Reveal Phone",
          API_GET_COMPANY_FULL: "Get Company Full",
          API_AI_SEARCH_EMPLOYEES: "AI Search Employees",
          API_SEARCH_EMPLOYEES: "Search Employees",
          SUBSCRIPTION_RESET: "Subscription Reset",
        };

        if (!history.type) return null;

        const label = typeLabels[history.type] ?? history.type;

        return (
          <Badge variant="outline" className="text-xs">
            {label}
          </Badge>
        );
      },
    },
    {
      id: "User",
      accessorKey: "userId",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t(
            "admin.dataTableAdminTeams.dialogs.giftCredits.history.columns.user"
          )}
        />
      ),
      cell: ({ cell }) => {
        const history = cell.row.original as TeamCreditsHistoryDTO;
        return <DataTableCellTeamCreditsHistoryUser history={history} />;
      },
    },
    {
      id: "Amount",
      accessorKey: "amount",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t(
            "admin.dataTableAdminTeams.dialogs.giftCredits.history.columns.amount"
          )}
        />
      ),
      cell: ({ cell }) => {
        const history = cell.row.original as TeamCreditsHistoryDTO;
        const isAdd = history.action === "ADD";
        return (
          <span className={isAdd ? "text-green-600" : "text-destructive"}>
            {isAdd ? "+" : "-"}
            {Math.abs(history.amount)}
          </span>
        );
      },
    },
    {
      id: "BalanceBefore",
      accessorKey: "balanceBefore",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t(
            "admin.dataTableAdminTeams.dialogs.giftCredits.history.columns.balanceBefore"
          )}
        />
      ),
      cell: ({ cell }) => {
        const history = cell.row.original as TeamCreditsHistoryDTO;
        return history.balanceBefore;
      },
    },
    {
      id: "BalanceAfter",
      accessorKey: "balanceAfter",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t(
            "admin.dataTableAdminTeams.dialogs.giftCredits.history.columns.balanceAfter"
          )}
        />
      ),
      cell: ({ cell }) => {
        const history = cell.row.original as TeamCreditsHistoryDTO;
        return history.balanceAfter;
      },
    },
    {
      id: "AdditionalData",
      accessorKey: "additionalData",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t(
            "admin.dataTableAdminTeams.dialogs.giftCredits.history.columns.additionalData"
          )}
        />
      ),
      cell: ({ cell }) => {
        const history = cell.row.original as TeamCreditsHistoryDTO;
        const additionalData = JSON.stringify(history.additionalData);
        if (additionalData === "{}") {
          return null;
        }

        const truncatedData =
          additionalData.length > 100
            ? additionalData.substring(0, 100) + "..."
            : additionalData;

        return (
          <>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon
                    className="w-4 h-4 cursor-pointer text-muted-foreground hover:text-foreground"
                    onClick={() => handleOpenModal(history.additionalData)}
                  />
                </TooltipTrigger>

                <TooltipContent>
                  <span>{truncatedData}</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <DialogAdditionalData
              additionalData={selectedAdditionalData}
              open={isModalOpen}
              onClose={handleCloseModal}
            />
          </>
        );
      },
    },
    {
      id: "Actions",
      header: () => (
        <div className="text-center">
          {t(
            "admin.dataTableAdminTeams.dialogs.giftCredits.history.columns.actions"
          )}
        </div>
      ),
      cell: ({ cell }) => {
        const history = cell.row.original as TeamCreditsHistoryDTO;
        const canRemove = history.action === "ADD" && onRemove;
        const hasPaymentId = !!history.paymentId;

        if (!canRemove && !hasPaymentId) {
          return null;
        }

        const handleOpenStripeDashboard = () => {
          if (history.paymentId) {
            const stripeUrl = `https://dashboard.stripe.com/payments/${history.paymentId}`;
            window.open(stripeUrl, "_blank", "noopener,noreferrer");
          }
        };

        return (
          <div className="flex justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  {t(
                    "admin.dataTableAdminTeams.dialogs.giftCredits.history.actions.title"
                  )}
                  <ChevronDownIcon className="ml-1 w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {hasPaymentId && (
                  <DropdownMenuItem
                    onClick={handleOpenStripeDashboard}
                    className="flex gap-2 items-center cursor-pointer"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t(
                      "admin.dataTableAdminTeams.dialogs.giftCredits.history.actions.viewStripePayment"
                    )}
                  </DropdownMenuItem>
                )}
                {canRemove && (
                  <DropdownMenuItem
                    onClick={() => onRemove(history)}
                    disabled={history.additionalData.removed}
                    className="flex gap-2 items-center cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
                  >
                    <Trash2Icon className="w-4 h-4" />
                    {t(
                      "admin.dataTableAdminTeams.dialogs.giftCredits.history.actions.delete"
                    )}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];
};
